import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';

/**
 * The people using a published app.
 *
 * Anyone who signs in to an app is enrolled on first use, so this is simply who has turned
 * up. It is the owner's view of their own audience — the counterpart to Replies, which is
 * what people sent rather than who they are.
 *
 * Only the owner (or an admin they appointed) can read it; the server enforces that.
 */
export interface AppUser {
  userId: string;
  email?: string;
  role: string;
  joinedAt?: string;
  lastActiveAt?: string;
}

export type AppUsersFailure = 'not-signed-in' | 'no-backend' | 'not-published' | 'not-allowed' | 'request-failed';

export class AppUsersError extends Error {
  readonly reason: AppUsersFailure;
  constructor(reason: AppUsersFailure) {
    super(MESSAGES[reason]);
    this.name = 'AppUsersError';
    this.reason = reason;
  }
}

const MESSAGES: Record<AppUsersFailure, string> = {
  'not-signed-in': 'Sign in to see who is using this app.',
  'no-backend': 'No backend is configured, so there is nobody to list.',
  'not-published': 'Publish this app first — people can only sign in once it is live.',
  'not-allowed': 'Only the owner of an app can see who is using it.',
  'request-failed': "Couldn't load the people using this app.",
};

function base(): string {
  const url = (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (!url) throw new AppUsersError('no-backend');
  return url;
}

function authHeader(): Record<string, string> {
  const token = CognitoService.getAccessTokenSync();
  if (!token) throw new AppUsersError('not-signed-in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Most recently active first — the question is usually "is anyone still using this". */
export async function fetchAppUsers(
  hostedAppId: string | undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<AppUser[]> {
  const baseUrl = base();
  if (!hostedAppId) throw new AppUsersError('not-published');
  // Resolved BEFORE the try: inside it, "you are signed out" was caught by the network
  // catch and reported as a generic failure, which is the one thing it is not.
  const headers = authHeader();

  let res: Response;
  try {
    res = await fetchImpl(`${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/members`, { headers });
  } catch {
    throw new AppUsersError('request-failed');
  }
  if (res.status === 403) throw new AppUsersError('not-allowed');
  if (!res.ok) throw new AppUsersError('request-failed');

  const body = (await res.json()) as { items?: AppUser[] };
  return (body.items ?? [])
    .slice()
    .sort((a, b) => String(b.lastActiveAt ?? b.joinedAt ?? '').localeCompare(String(a.lastActiveAt ?? a.joinedAt ?? '')));
}

/**
 * Remove someone from the app, taking the data it holds about them with them.
 *
 * Not the same as revoking access — that is `setAppUserRole(..., 'blocked')`, which keeps
 * their data and stops them using the app. Removing means removing.
 */
export async function removeAppUser(
  hostedAppId: string | undefined,
  userId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ records: number }> {
  const baseUrl = base();
  if (!hostedAppId) throw new AppUsersError('not-published');
  const headers = authHeader();

  let res: Response;
  try {
    res = await fetchImpl(
      `${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers },
    );
  } catch {
    throw new AppUsersError('request-failed');
  }
  if (res.status === 403) throw new AppUsersError('not-allowed');
  if (!res.ok) throw new AppUsersError('request-failed');

  const body = (await res.json()) as { records?: number };
  return { records: body.records ?? 0 };
}

/** Block someone (or let them back in) without touching what they have stored. */
export async function setAppUserRole(
  hostedAppId: string | undefined,
  userId: string,
  role: 'user' | 'blocked' | 'admin',
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const baseUrl = base();
  if (!hostedAppId) throw new AppUsersError('not-published');
  const headers = authHeader();

  let res: Response;
  try {
    res = await fetchImpl(
      `${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PUT', headers, body: JSON.stringify({ role }) },
    );
  } catch {
    throw new AppUsersError('request-failed');
  }
  if (res.status === 403) throw new AppUsersError('not-allowed');
  if (!res.ok) throw new AppUsersError('request-failed');
}

/** "3 days ago" — an exact timestamp is noise when the question is "recently?". */
export function lastSeen(user: AppUser, now: Date = new Date()): string {
  const when = user.lastActiveAt ?? user.joinedAt;
  if (!when) return 'never';
  const days = Math.floor((now.getTime() - new Date(when).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return 'just now';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}
